import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { IoMdArrowBack } from "react-icons/io";
import { FiEdit3, FiTag, FiPhone, FiMapPin, FiInfo, FiChevronRight } from "react-icons/fi";
import * as api from "../../api/index";

const lagosLGAs = [
  "Agege", "Ajeromi-Ifelodun", "Alimosho", "Amuwo-Odofin", "Apapa", "Badagry",
  "Epe", "Eti-Osa", "Ibeju-Lekki", "Ifako-Ijaiye", "Ikeja", "Ikorodu", "Kosofe",
  "Lagos Island", "Lagos Mainland", "Mushin", "Ojo", "Oshodi-Isolo",
  "Shomolu", "Surulere",
];

export default function EditProduct() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [form, setForm] = useState({
    name: "",
    description: "",
    price: "",
    phone: "",
    lga: "",
  });

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const res = await api.getSingleProduct(id);
        if (res.data.success) {
          const p = res.data.product;
          setForm({
            name: p.name,
            description: p.description,
            price: p.price,
            phone: p.phone,
            lga: p.location?.lga || "",
          });
        }
      } catch (err) {
        console.error("❌ Error loading product:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");
    
    if (form.description.trim().length < 10) {
      setSubmitError("Description must be at least 10 characters long.");
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("description", form.description);
      formData.append("price", form.price);
      formData.append("phone", form.phone);
      formData.append("lga", form.lga);

      const res = await api.updateProduct(id, formData, {
        onUploadProgress: (event) => {
          const percent = Math.round((event.loaded * 100) / event.total);
          setUploadProgress(percent);
        },
      });

      if (res.data.success) {
        navigate(-1);
      } else {
        setSubmitError("Failed to update product.");
      }
    } catch (err) {
      setSubmitError("Error updating product. Please try again.");
    } finally {
      setSubmitting(false);
      setUploadProgress(0);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">Loading Product...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex flex-col relative overflow-hidden">
      {/* Decorative Blur */}
      <div className="absolute top-[-5%] right-[-5%] w-72 h-72 bg-orange-100 rounded-full blur-3xl opacity-40"></div>

      {/* Header */}
      <div className="bg-white px-6 py-6 flex items-center shadow-sm border-b border-gray-100 relative z-20">
        <button
          onClick={() => navigate(-1)}
          className="p-2 -ml-2 hover:bg-gray-100 rounded-full transition text-gray-900"
        >
          <IoMdArrowBack size={24} />
        </button>
        <div className="ml-4">
          <h1 className="text-xs font-black uppercase tracking-[0.2em] text-orange-600">Inventory</h1>
          <p className="text-lg font-black text-gray-900 tracking-tighter">Edit Listing</p>
        </div>
      </div>

      <div className="flex-1 p-4 max-w-2xl mx-auto w-full relative z-10 pt-8 pb-12">
        <form onSubmit={handleSubmit} className="bg-white rounded-[2.5rem] p-6 md:p-10 shadow-xl shadow-gray-200/50 border border-gray-100 space-y-8">
          
          {/* Read Only Name Section */}
          <div className="bg-gray-50 p-5 rounded-2xl border border-gray-100">
            <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1 block">Product Title (Fixed)</label>
            <div className="flex items-center gap-2 text-gray-900 font-bold">
              <FiTag className="text-gray-400" />
              <span>{form.name}</span>
            </div>
          </div>

          <div className="space-y-6">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 bg-orange-50 rounded-lg flex items-center justify-center text-orange-600">
                <FiEdit3 size={16} />
              </div>
              <h3 className="text-xs font-black uppercase tracking-widest text-gray-900">Update Details</h3>
            </div>

            {/* Description */}
            <div className="space-y-1">
              <label className="text-[10px] font-black text-gray-400 uppercase ml-1">Detailed Description</label>
              <div className="relative">
                <FiInfo className="absolute left-4 top-4 text-gray-400" />
                <textarea
                  name="description"
                  value={form.description}
                  onChange={handleChange}
                  placeholder="Tell us about the product..."
                  className="w-full pl-12 pr-4 py-4 bg-gray-50 border-transparent rounded-2xl focus:bg-white focus:border-orange-500 transition-all text-sm font-bold outline-none min-h-[120px] resize-none"
                  required
                />
              </div>
            </div>

            {/* Price & Phone Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="text-[10px] font-black text-gray-400 uppercase ml-1">Price (₦)</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-900 font-black text-sm">₦</span>
                  <input
                    type="number"
                    name="price"
                    value={form.price}
                    onChange={handleChange}
                    className="w-full pl-10 pr-4 py-4 bg-gray-50 border-transparent rounded-2xl focus:bg-white focus:border-orange-500 transition-all text-sm font-bold outline-none"
                    required
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-[10px] font-black text-gray-400 uppercase ml-1">Contact Phone</label>
                <div className="relative">
                  <FiPhone className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    className="w-full pl-12 pr-4 py-4 bg-gray-50 border-transparent rounded-2xl focus:bg-white focus:border-orange-500 transition-all text-sm font-bold outline-none"
                    required
                  />
                </div>
              </div>
            </div>

            {/* LGA Select */}
            <div className="space-y-1">
              <label className="text-[10px] font-black text-gray-400 uppercase ml-1">Lagos Location (LGA)</label>
              <div className="relative">
                <FiMapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 z-10" />
                <select
                  name="lga"
                  value={form.lga}
                  onChange={handleChange}
                  className="w-full pl-12 pr-10 py-4 bg-gray-50 border-transparent rounded-2xl focus:bg-white focus:border-orange-500 transition-all text-sm font-bold appearance-none cursor-pointer outline-none"
                  required
                >
                  <option value="">Select LGA</option>
                  {lagosLGAs.map((l) => (
                    <option key={l} value={l}>{l}</option>
                  ))}
                </select>
                <FiChevronRight className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 rotate-90 pointer-events-none" />
              </div>
            </div>
          </div>

          {submitError && (
            <div className="p-4 rounded-2xl bg-red-50 border border-red-100 text-red-600 text-[10px] font-black uppercase tracking-wider text-center">
              {submitError}
            </div>
          )}

          {/* Submit Action */}
          <div className="pt-4">
            <button
              type="submit"
              disabled={submitting}
              className={`w-full py-5 rounded-2xl font-black text-xs uppercase tracking-[0.25em] flex items-center justify-center gap-4 shadow-xl transition-all active:scale-95 ${
                submitting ? "bg-orange-100 text-orange-400 cursor-not-allowed" : "bg-gray-900 text-white hover:bg-black shadow-gray-200"
              }`}
            >
              {submitting ? (
                <>
                  <div className="w-5 h-5 border-2 border-orange-400 border-t-transparent rounded-full animate-spin"></div>
                  Updating {uploadProgress > 0 && `${uploadProgress}%`}
                </>
              ) : (
                <>
                  Save Changes
                  <FiChevronRight size={18} />
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}